import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "overlay", "drawer", "countryInput", "countryResults", "fdrawer", "countrySlug"]

  connect() {
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("mousedown", this.handleClickOutside)
    if (this.hasCountryInputTarget) {
      this.countryInputTarget.addEventListener("focus", this.showCountryResultsOnFocus.bind(this))
    }
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    if (this.hasCountryInputTarget) {
      this.countryInputTarget.removeEventListener("focus", this.showCountryResultsOnFocus)
    }
  }

  open() {
    this.fdrawerTarget.style.display = ""
    document.body.style.overflow = "hidden"
  }
  
  close(event) {
    // Always close if called from button (no event or event.target is button)
    if (!event || event.target.closest('button')) {
      this.fdrawerTarget.style.display = "none"
      document.body.style.overflow = ""
      return
    }
    // Only close if clicking directly on overlay
    if (event.target === this.overlayTarget) {
      this.fdrawerTarget.style.display = "none"
      document.body.style.overflow = ""
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  apply(event) {
    event.preventDefault();
    const form = this.drawerTarget.querySelector("form");
    if (!form) return;
  
    const countryRadio = form.querySelector('input[name="country"]:checked');
    const country = countryRadio ? countryRadio.value : null;
    const countrySlug = this.hasCountrySlugTarget ? this.countrySlugTarget.value : "";
  
    const productTypes = Array.from(form.querySelectorAll('input[name="product_types[]"]:checked')).map(cb => cb.value);
  
    let url = "";
    let params = [];
  
    if (country === "anywhere" || !country) {
      url = "/cultural-souvenirs";
    } else if (country === "another") {
      // User searched for a region, could be country/state/city
      const slugParts = (countrySlug || "").split("/").filter(Boolean);
      const countryPart = slugParts[0];
      const statePart = slugParts[1];
      const cityPart = slugParts[2];
  
      url = `/cultural-souvenirs/${encodeURIComponent(countryPart)}`;
      if (statePart) params.push(`state=${encodeURIComponent(statePart)}`);
      if (cityPart) params.push(`city=${encodeURIComponent(cityPart)}`);
    } else if (countryRadio && countryRadio.dataset.slug) {
      // Preset country radio: just use the slug in the path
      url = `/cultural-souvenirs/${countryRadio.dataset.slug}`;
    } else {
      url = "/cultural-souvenirs";
    }
  
    if (productTypes.length > 0) {
      params.push(...productTypes.map(pt => `product_types[]=${encodeURIComponent(pt)}`));
    }
  
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
  
    window.location.href = url;
    this.close();
  }

  clearAll(event) {
    event.preventDefault()
    const form = this.drawerTarget.querySelector("form")
    if (form) {
      form.reset()
  
      // Explicitly uncheck all product type checkboxes
      form.querySelectorAll('input[type="checkbox"][name="product_types[]"]').forEach(cb => {
        cb.checked = false
      })
  
      // Explicitly uncheck all country radios
      form.querySelectorAll('input[type="radio"][name="country"]').forEach(rb => {
        rb.checked = false
      })
  
      // Disable and clear country input
      if (this.hasCountryInputTarget) {
        this.countryInputTarget.disabled = true
        this.countryInputTarget.value = ""
      }
    }
  
    // Clear country results dropdown
    if (this.hasCountryResultsTarget) {
      this.countryResultsTarget.innerHTML = ""
      this.countryResultsTarget.style.display = "none"
    }
  }

  searchCountry(event) {
    const input = event.target;
    const query = input.value.trim();
    const url = input.getAttribute('data-autocomplete-url');
    const countrySlug = input.getAttribute('data-country-slug');

    let fetchUrl = url;
    if (countrySlug) {
      fetchUrl += (fetchUrl.includes('?') ? '&' : '?') + 'country_slug=' + encodeURIComponent(countrySlug);
    }
    fetchUrl += (fetchUrl.includes('?') ? '&' : '?') + 'q=' + encodeURIComponent(query);

    fetch(fetchUrl)
      .then(response => response.json())
      .then(data => {
        if (!Array.isArray(data)) return
        this.countryResultsTarget.innerHTML = data.map(region =>
          `<div class="px-3 py-2 hover:bg-gray-100 cursor-pointer" data-id="${region.id}" data-name="${region.name}" data-slug="${region.slug}" data-region-type="${region.region_type}" data-action="mousedown->filter-drawer#selectCountry">${region.name}</div>`
        ).join("")
        this.countryResultsTarget.style.display = "block"
      })
  }

  selectCountry(event) {
    const name = event.target.dataset.name
    const slug = event.target.dataset.slug
    const regionType = event.target.dataset.regionType
    this.countryInputTarget.value = name
    if (this.hasCountrySlugTarget) {
      this.countrySlugTarget.value = slug || ""
      this.countrySlugTarget.dataset.regionType = regionType || ""
    }
    this.countryResultsTarget.innerHTML = ""
    this.countryResultsTarget.style.display = "none"
  }

  handleClickOutside(event) {
    if (
      this.hasCountryResultsTarget &&
      !this.countryResultsTarget.contains(event.target) &&
      !this.countryInputTarget.contains(event.target)
    ) {
      this.countryResultsTarget.style.display = "none"
    }
  }

  showCountryResultsOnFocus() {
    if (this.countryResultsTarget.innerHTML.trim() !== "") {
      this.countryResultsTarget.style.display = "block"
    }
  }

  countryRadioChanged(event) {
    const anotherRadio = this.countryInputTarget.form.querySelector('#country_another')
    if (anotherRadio.checked) {
      this.countryInputTarget.readOnly = false
    } else {
      this.countryInputTarget.readOnly = true
      this.countryInputTarget.value = ""
      this.countryResultsTarget.innerHTML = ""
      this.countryResultsTarget.style.display = "none"
    }
  }

  focusCountryInput(event) {
    // Select the "Another location" radio button
    const form = this.drawerTarget.querySelector("form");
    const anotherRadio = form.querySelector('input[type="radio"][name="country"][value="another"]');
    if (anotherRadio && !anotherRadio.checked) {
      anotherRadio.checked = true;
      anotherRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Remove readonly if it was set
    event.target.readOnly = false;
  }
}